import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Clapperboard, LineChart, Mic, GraduationCap, Send, Rocket, PlaneTakeoff, PlaneLanding } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { localeUpperCase } from '../../lib/textCase';
import useReducedMotion from '../../hooks/useReducedMotion';

// Mockup'ta 3. adım "Canlı Yayınlara Katıl" idi — sistemde canlı yayın
// özelliği bulunmadığı için (karar gerekli — bkz. rapor) gerçek bir özelliğe,
// yol haritası/ilerleme takibine (/programs/kabin-memurlugu/roadmap) bağlandı.
const STEPS = [
  // Self-register kapatildi (SELF_REGISTER_AKTIF=false) — step1 artik /login'e
  // gider, i18n metni ('kaydol' -> 'giris yap') buna gore guncellendi.
  { key: 'step1', icon: Target, to: '/login', accent: '#34CC67', accentText: '#08331d' },
  { key: 'step2', icon: Clapperboard, to: '/courses', accent: '#7FB6D9', accentText: '#0B1A33' },
  { key: 'step3', icon: LineChart, to: '/programs/kabin-memurlugu/roadmap', accent: '#7FB6D9', accentText: '#0B1A33' },
  { key: 'step4', icon: Mic, to: '/programs/kabin-memurlugu/interview', accent: '#C9A227', accentText: '#241c02' },
  { key: 'step5', icon: GraduationCap, to: '/certificates', accent: '#C9A227', accentText: '#241c02' },
  { key: 'step6', icon: Send, to: '/programs/kabin-memurlugu/cv', accent: '#7FB6D9', accentText: '#0B1A33' },
  { key: 'step7', icon: Rocket, to: '/courses', accent: '#34CC67', accentText: '#08331d' },
];

// Dev-only invariant: ROW_PATHS sadece STEPS.length === 7 (4+3 satır dağılımı)
// için tanımlı. STEPS ileride değişirse rail sessizce kaybolmasın diye uyar.
if (process.env.NODE_ENV !== 'production' && STEPS.length !== 7) {
  console.warn(
    `[RoadmapSteps] STEPS.length=${STEPS.length} (beklenen: 7). ROW_PATHS yalnızca 4 ve 3 satır ` +
      'için tanımlı; satır dağılımı değiştiyse ROW_PATHS ve row1/row2 slice mantığını güncelleyin.'
  );
}

const RAIL_STROKE = {
  stroke: '#C9A227',
  strokeWidth: '2.5',
  strokeDasharray: '1 7',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  fill: 'none',
};

// Uçuş rotası "rail" katmanı: satırın üzerinden geçen tek SVG. Waypoint
// x-merkezleri n eşit hücre için x_i = (2i+1)*1200/(2n) formülüyle grid hücre
// merkeziyle matematiksel olarak örtüşür, JS ölçüm gerektirmez.
const ROW_PATHS = {
  4: 'M150 50 C250 20 350 80 450 50 C550 20 650 80 750 50 C850 20 950 80 1050 50',
  3: 'M200 50 C333 20 467 80 600 50 C733 20 867 80 1000 50',
};

// Yatay rota (lg+): satır kartlarının üstünden geçen kesikli çizgi. Reduced
// motion'da doğrudan tam çizili (statik) render edilir, animasyon atlanır.
function RouteRail({ count, reduced }) {
  const d = ROW_PATHS[count];
  if (!d) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[RoadmapSteps] ROW_PATHS[${count}] tanımlı değil — rail bu satır için sessizce render edilmiyor. ` +
          'STEPS satır dağılımı (4/3) değiştiyse ROW_PATHS sözlüğünü güncelleyin.'
      );
    }
    return null;
  }
  return (
    <svg
      viewBox="0 0 1200 100"
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      className="absolute -top-6 left-0 w-full h-6 lg:h-8 pointer-events-none hidden lg:block"
      aria-hidden="true"
    >
      <motion.path
        d={d}
        {...RAIL_STROKE}
        strokeOpacity="0.45"
        initial={reduced ? { pathLength: 1 } : { pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
      />
    </svg>
  );
}

// Satır 1 sonu -> satır 2 başı geçiş: S-eğrisi ile alt satıra iner (lg+).
function RouteTransitionArc({ reduced }) {
  return (
    <div className="relative h-16 lg:h-10 hidden lg:block" aria-hidden="true">
      <svg viewBox="0 0 1200 160" preserveAspectRatio="none" width="100%" height="100%" className="absolute inset-0">
        <motion.path
          d="M1050 0 C1050 90 200 70 200 160"
          {...RAIL_STROKE}
          strokeOpacity="0.55"
          initial={reduced ? { pathLength: 1 } : { pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, ease: 'easeOut', delay: 0.15 }}
        />
      </svg>
    </div>
  );
}

// Dikey rota (lg altı — mobil ve tablet): 7 waypoint kesintisiz tek rail'de
// akar, satır arası ayrı geçiş eğrisi gerekmez.
function VerticalRouteRail({ reduced }) {
  return (
    <svg
      viewBox="0 0 100 700"
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      className="absolute left-6 top-0 h-full w-6 pointer-events-none lg:hidden"
      aria-hidden="true"
    >
      <motion.path
        d="M50 50 C58 100 42 150 50 200 C58 250 42 300 50 350 C58 400 42 450 50 500 C58 550 42 600 50 650"
        {...RAIL_STROKE}
        strokeOpacity="0.45"
        initial={reduced ? { pathLength: 1 } : { pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
      />
    </svg>
  );
}

function StepCard({ step, num, variant }) {
  const { t } = useLanguage();
  const Icon = step.icon;
  const isHorizontal = variant === 'horizontal';
  const badgeClassName = isHorizontal
    ? 'absolute -top-[22px] left-1/2 -translate-x-1/2 w-11 h-11 rounded-full flex items-center justify-center font-black text-[19px] ring-2 ring-[#C9A227]/70 ring-offset-2 ring-offset-[#0d213f] shadow-[0_4px_10px_rgba(0,0,0,.35)]'
    : 'w-11 h-11 rounded-full flex items-center justify-center font-black text-[19px] ring-2 ring-[#C9A227]/70 ring-offset-2 ring-offset-[#0d213f] shadow-[0_4px_10px_rgba(0,0,0,.35)]';

  return (
    <li className={`list-none relative flex-1 ${isHorizontal ? 'px-3' : ''}`}>
      <Link
        to={step.to}
        className={`group relative flex flex-1 min-w-[220px] h-full flex-col bg-[#0b1c38] border border-white/10 rounded-2xl ${isHorizontal ? 'p-5 pt-8' : 'p-5'} hover:border-[#C9A227]/50 hover:-translate-y-1 transition-[transform,border-color] duration-200 ease-out motion-reduce:hover:translate-y-0 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C9A227]`}
        data-testid={isHorizontal ? `roadmap-${step.key}` : `roadmap-${step.key}-mobile`}
        aria-label={`${num}. ${t(`landing2.roadmap.${step.key}.title`)}`}
      >
        <div aria-hidden="true" className={badgeClassName} style={{ background: step.accent, color: step.accentText }}>
          {num}
        </div>
        <div className="my-3.5" aria-hidden="true">
          <Icon size={26} className="text-[#F7F5F0]/80" />
        </div>
        <h3 className="font-extrabold text-[#F7F5F0] text-[16px] mb-2" aria-hidden="true">
          {t(`landing2.roadmap.${step.key}.title`)}
        </h3>
        <p className="text-[13.5px] leading-relaxed text-[#b0bccc]">{t(`landing2.roadmap.${step.key}.desc`)}</p>
      </Link>
    </li>
  );
}

export default function RoadmapSteps() {
  const { t, lang } = useLanguage();
  const reduced = useReducedMotion();
  const row1 = STEPS.slice(0, 4);
  const row2 = STEPS.slice(4);

  return (
    <section className="bg-gradient-to-br from-[#0d213f] to-[#0B1A33] py-16 md:py-20" data-testid="landing2-roadmap">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-lg mb-11">
          <div className="text-xs font-bold tracking-widest text-[#C9A227]">
            {localeUpperCase(t('landing2.roadmap.eyebrow'), lang)}
          </div>
          <h2 className="landing2-display text-3xl md:text-[40px] mt-3 text-[#F7F5F0]">
            {localeUpperCase(t('landing2.roadmap.title'), lang)}
          </h2>
          <p className="text-[17px] text-[#c5cfdd] mt-4 leading-relaxed">
            {t('landing2.roadmap.subtitle')}
          </p>
        </div>

        <ol data-testid="roadmap-route" aria-label={t('landing2.roadmap.title')} className="list-none m-0 p-0">
          {/* Mobil/tablet (lg altı): tek kolon, 7 waypoint kesintisiz tek dikey rail'de */}
          <div className="relative lg:hidden pl-2 pt-6" role="presentation">
            <VerticalRouteRail reduced={reduced} />
            <PlaneTakeoff
              size={20}
              strokeWidth={2.5}
              className="text-[#C9A227] absolute left-6 -top-3 z-10"
              style={{ transform: 'rotate(90deg)' }}
              aria-hidden="true"
            />
            <PlaneLanding
              size={20}
              strokeWidth={2.5}
              className="text-[#C9A227] absolute left-6 -bottom-3 z-10"
              style={{ transform: 'rotate(100deg)' }}
              aria-hidden="true"
            />
            <div className="flex flex-col gap-y-10" role="presentation">
              {STEPS.map((s, i) => (
                <StepCard key={s.key} step={s} num={i + 1} variant="vertical" />
              ))}
            </div>
          </div>

          {/* Masaüstü (lg+): satır 1 (4 waypoint) + S-eğrisi geçiş + satır 2 (3 waypoint) */}
          <div className="hidden lg:block" role="presentation">
            <div className="relative flex flex-col lg:flex-row lg:items-stretch gap-y-10 lg:gap-y-0 pt-6" role="presentation">
              <PlaneTakeoff
                size={20}
                strokeWidth={2.5}
                className="text-[#C9A227] absolute -left-1 md:-left-2 -top-2"
                style={{ transform: 'rotate(-20deg)' }}
                aria-hidden="true"
              />
              <RouteRail count={4} reduced={reduced} />
              {row1.map((s, i) => (
                <StepCard key={s.key} step={s} num={i + 1} variant="horizontal" />
              ))}
            </div>

            <RouteTransitionArc reduced={reduced} />

            <div className="relative flex flex-col lg:flex-row lg:items-stretch gap-y-10 lg:gap-y-0" role="presentation">
              <RouteRail count={3} reduced={reduced} />
              {row2.map((s, i) => (
                <StepCard key={s.key} step={s} num={i + 5} variant="horizontal" />
              ))}
              <PlaneLanding
                size={20}
                strokeWidth={2.5}
                className="text-[#C9A227] absolute -right-1 md:-right-2 -top-2"
                style={{ transform: 'rotate(15deg)' }}
                aria-hidden="true"
              />
            </div>
          </div>
        </ol>
      </div>
    </section>
  );
}
